import React, { useContext } from 'react';
import {
  aggregateTaxAwarePrices,
  getClusivitySeparatedPrices,
} from '@vp/om-tax-display';
import { TaxAwarePriceRow } from '@vp/component-library';
import { useTranslations, ReplaceTokens } from '@vp/om-translations';
import { formatDate } from '@vp/om-date-formatter';
import { SelectionSetInput, SelectionSetLabel, Typography } from '@vp/swan';
import { TaxDisplayContext } from '../shared/taxDisplay/TaxDisplayContext';
import useSiteData from '../../hooks/useSiteData';
import type { ShippingOption } from '../../types';
import { overrideMerchandisingDisplayDataIfNecessary } from '../../util/shippingOptionsHelper';

export const ShippingQuote = ({
  shippingOption,
}: {
  shippingOption: ShippingOption;
}) => {
  const { locale } = useSiteData();
  const { taxDisplayContext } = useContext(TaxDisplayContext) || {};
  const [shippingMessages] = useTranslations(['shippingMessages']);

  const localizedShippingOptionsDisplay =
    overrideMerchandisingDisplayDataIfNecessary(shippingOption, locale);
  const { name, description, displayTextOverrides } =
    localizedShippingOptionsDisplay;
  const arrivalDateTextOverride =
    displayTextOverrides && displayTextOverrides.ARRIVAL_DATE_TEXT
      ? displayTextOverrides.ARRIVAL_DATE_TEXT
      : null;

  const { pricingData, displayText } = shippingOption;
  const { earliestArrivalDate } = displayText || {};
  const {
    grossListCents,
    grossDiscountedCents,
    netListCents,
    netDiscountedCents,
    fractionDigits,
    currency: currencyCode,
    taxes,
  } = pricingData;

  const showShippingOptionNameOnly = !description;

  let shippingOptionLabel = name;
  if (!showShippingOptionNameOnly) {
    shippingOptionLabel += ` : ${description} `;
  }

  // flatten 'taxType' field of tax objects
  const formattedTaxes =
    (taxes &&
      taxes.map(tax => {
        const {
          taxType: { type },
          ...theRestOfTheTax
        } = tax;
        return {
          ...theRestOfTheTax,
          type,
        };
      })) ||
    [];

  // the array of "priced components" for a single shipping option is just one object of the cents
  const shippingOptionComponents = [
    {
      grossListCents,
      netListCents,
      grossDiscountedCents,
      netDiscountedCents,
    },
  ];
  const clusivitySeparatedShippingOptionPrice = getClusivitySeparatedPrices(
    shippingOptionComponents,
    formattedTaxes
  );
  const aggregatedShippingOptionPrice = aggregateTaxAwarePrices(
    [clusivitySeparatedShippingOptionPrice],
    'shipping',
    'detailed',
    [],
    taxDisplayContext
  );

  return (
    <SelectionSetInput
      value={shippingOption.shippingMethodId}
      data-testid={`${shippingOption.shippingMethodId}-shipping-method-selection`}
    >
      <SelectionSetLabel
        data-testid={`label-${shippingOption.shippingMethodId}-shipping-method-selection`}
        mb={2}
        p={2}
      >
        <TaxAwarePriceRow
          fractionDigits={fractionDigits}
          listCents={aggregatedShippingOptionPrice.taxAwareListCents}
          discountedCents={
            aggregatedShippingOptionPrice.taxAwareDiscountedCents
          }
          currencyCode={currencyCode}
          locale={locale}
          taxes={[
            ...aggregatedShippingOptionPrice.includedTaxes,
            ...aggregatedShippingOptionPrice.excludedTaxes,
          ]}
          rowLabel={shippingOptionLabel}
          labelFormat={{
            fontSize: -1,
            fontWeight: 'bold',
          }}
          priceFormat={{
            fontSize: -1,
          }}
        />
        <Typography component="span" textColor="dark-grey" fontSize={-1}>
          {arrivalDateTextOverride || (
            <ReplaceTokens
              tokenizedString={shippingMessages.arrivalBy}
              substitutions={{
                arrivalDate: formatDate(earliestArrivalDate, locale),
              }}
            />
          )}
        </Typography>
      </SelectionSetLabel>
    </SelectionSetInput>
  );
};
